import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { CircularProgress, IconButton, makeStyles } from '@material-ui/core'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import SydModal from '../../commonDesign/SydModal'
import SydModalActions from '../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../commonDesign/Button'
import Icon from '../../atoms/Icon'
import {
  useAssignClientExplorerConfiguration,
  useUnassignClientExplorerConfiguration,
  useReorderClientExplorerConfigurations
} from '../../../api/coreData'
import { useExplorerConfigurationsForClient } from './hooks/useExplorerConfigurationsForClient'

const DRAG_TYPE = 'SAVED_VIEW_ROW'
const ORDINAL_GAP = 10

const useStyles = makeStyles(() => ({
  scroll: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 200,
    maxHeight: 480,
    overflowY: 'auto'
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 8
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '12px 12px 6px',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--color-neutral-500, #888)'
  },
  sectionEmpty: {
    padding: '4px 16px 8px',
    fontSize: 12,
    color: 'var(--color-neutral-400, #aaa)',
    fontStyle: 'italic'
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    borderRadius: 'var(--border-radius-sm, 4px)',
    cursor: 'pointer',
    transition: 'background 120ms ease, opacity 120ms ease',
    '&:hover': {
      background: 'var(--theme-color-surface-d1, #f5f5f5)'
    }
  },
  rowActive: {
    background: 'var(--color-primary-050, #e3f2fd)',
    '&:hover': {
      background: 'var(--color-primary-100, #bbdefb)'
    }
  },
  rowDeleting: {
    opacity: 0.45,
    pointerEvents: 'none'
  },
  rowDragging: {
    opacity: 0.4
  },
  dragHandle: {
    flexShrink: 0,
    width: 18,
    height: 18,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-neutral-400, #aaa)',
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing'
    }
  },
  starSlot: {
    flexShrink: 0,
    width: 14,
    display: 'inline-flex',
    justifyContent: 'center'
  },
  defaultStar: {
    fontSize: 14,
    color: 'var(--color-warning-500, #f9a825)'
  },
  name: {
    flex: 1,
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--color-neutral-800, #333)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  rowAction: {
    flexShrink: 0
  },
  deleteBtn: {
    padding: 4,
    flexShrink: 0,
    opacity: 0.5,
    '&:hover': {
      opacity: 1
    }
  },
  empty: {
    padding: '32px 16px',
    textAlign: 'center',
    color: 'var(--color-neutral-400, #aaa)',
    fontSize: 14
  }
}))

function ConfigRow ({
  config,
  classes,
  isActive,
  isDeleting,
  showStar,
  onLoad,
  rowAction,
  trailingDeleteSlot
}) {
  return (
    <div
      className={clsx(classes.row, {
        [classes.rowActive]: isActive,
        [classes.rowDeleting]: isDeleting
      })}
      onClick={onLoad}
    >
      <span className={classes.starSlot}>
        {showStar ? <span className={classes.defaultStar}>&#9733;</span> : null}
      </span>
      <span className={classes.name}>{config.name}</span>
      {rowAction ? <span className={classes.rowAction}>{rowAction}</span> : null}
      {isDeleting ? <CircularProgress size={14} /> : trailingDeleteSlot}
    </div>
  )
}

ConfigRow.propTypes = {
  config: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  isActive: PropTypes.bool,
  isDeleting: PropTypes.bool,
  showStar: PropTypes.bool,
  onLoad: PropTypes.func.isRequired,
  rowAction: PropTypes.node,
  trailingDeleteSlot: PropTypes.node
}

/**
 * Same shape as ConfigRow plus a drag handle and react-dnd hover/drop
 * wiring. `moveItem` is called repeatedly during hover so the order
 * updates in real time; the parent commits the final ordering on
 * `onDropEnd` (a no-op until the user releases).
 */
function DraggableConfigRow ({
  config,
  index,
  classes,
  isActive,
  showStar,
  onLoad,
  rowAction,
  moveItem,
  onDropEnd
}) {
  const ref = useRef(null)

  const [{ isDragging }, drag, preview] = useDrag({
    type: DRAG_TYPE,
    item: () => ({ index, id: config.explorerConfigurationId }),
    end: () => onDropEnd(),
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  })

  const [, drop] = useDrop({
    accept: DRAG_TYPE,
    hover (item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    }
  })

  preview(drop(ref))

  return (
    <div
      ref={ref}
      className={clsx(classes.row, {
        [classes.rowActive]: isActive,
        [classes.rowDragging]: isDragging
      })}
      onClick={onLoad}
    >
      <span
        ref={drag}
        className={classes.dragHandle}
        onClick={(e) => e.stopPropagation()}
        title='Drag to reorder'
      >
        <Icon name='dragIndicator' customSize={14} />
      </span>
      <span className={classes.starSlot}>
        {showStar ? <span className={classes.defaultStar}>&#9733;</span> : null}
      </span>
      <span className={classes.name}>{config.name}</span>
      {rowAction ? <span className={classes.rowAction}>{rowAction}</span> : null}
    </div>
  )
}

DraggableConfigRow.propTypes = {
  config: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  classes: PropTypes.object.isRequired,
  isActive: PropTypes.bool,
  showStar: PropTypes.bool,
  onLoad: PropTypes.func.isRequired,
  rowAction: PropTypes.node,
  moveItem: PropTypes.func.isRequired,
  onDropEnd: PropTypes.func.isRequired
}

export default function OpenViewDialog ({
  open,
  onClose,
  clientId,
  clientName,
  activeConfigId,
  onLoadConfig,
  onDeleteConfig
}) {
  const classes = useStyles()
  const [deletingId, setDeletingId] = useState(null)
  const [pendingAssignId, setPendingAssignId] = useState(null)
  const [pendingUnassignId, setPendingUnassignId] = useState(null)

  // We intentionally ignore the `otherClients` bucket the hook returns:
  // views the current user has saved for some other client (but not the
  // current one) are hidden from this picker entirely. At scale a popular
  // view could be assigned to hundreds of clients, and surfacing them
  // here adds noise without an obvious win -- the user can still find
  // them by switching client context.
  const { thisClient, myViews, isLoading } =
    useExplorerConfigurationsForClient(clientId)

  const assignMutation = useAssignClientExplorerConfiguration()
  const unassignMutation = useUnassignClientExplorerConfiguration()
  const reorderMutation = useReorderClientExplorerConfigurations()

  const handleClose = useCallback(() => {
    setDeletingId(null)
    setPendingAssignId(null)
    setPendingUnassignId(null)
    onClose()
  }, [onClose])

  const handleLoad = useCallback((config) => {
    onLoadConfig(config)
    handleClose()
  }, [onLoadConfig, handleClose])

  const handleDelete = useCallback((e, config) => {
    e.stopPropagation()
    if (!window.confirm(`Delete "${config.name}"?`)) return
    setDeletingId(config.explorerConfigurationId)
    onDeleteConfig(config.explorerConfigurationId)
  }, [onDeleteConfig])

  const handleSaveForClient = useCallback((e, config) => {
    e.stopPropagation()
    if (!clientId || pendingAssignId) return
    setPendingAssignId(config.explorerConfigurationId)
    assignMutation.mutate(
      { explorerConfigurationId: config.explorerConfigurationId, clientId },
      { onSettled: () => setPendingAssignId(null) }
    )
  }, [clientId, assignMutation, pendingAssignId])

  const handleRemoveFromClient = useCallback((e, config) => {
    e.stopPropagation()
    if (!clientId || pendingUnassignId) return
    setPendingUnassignId(config.explorerConfigurationId)
    unassignMutation.mutate(
      { explorerConfigurationId: config.explorerConfigurationId, clientId },
      { onSettled: () => setPendingUnassignId(null) }
    )
  }, [clientId, unassignMutation, pendingUnassignId])

  // Reorder commit: one server round-trip, one cache invalidation.
  // The backend's `reorder-client-explorer-configurations` command
  // takes the whole reorder as a single atomic multi-row upsert, so we
  // don't fan out N parallel assigns from the client (which would each
  // invalidate the configurations query independently and risk a
  // refetch storm). On failure we just log and return -- the server is
  // the source of truth, and either the call succeeded and the
  // invalidation refetched the new state, or it failed and the next
  // organic refetch will reconcile the picker to what's actually on
  // disk. Returns a promise so the section can clear its local
  // optimistic order once it resolves.
  const handleReorderCommit = useCallback(async (updates) => {
    if (!clientId || updates.length === 0) return
    try {
      await reorderMutation.mutateAsync({
        clientId,
        orderings: updates.map(({ id, ordinal }) => ({
          explorerConfigurationId: id,
          ordinal
        }))
      })
    } catch (err) {
      console.error('Reorder failed', err)
    }
  }, [clientId, reorderMutation])

  const isEmpty = !isLoading &&
    thisClient.length === 0 &&
    myViews.length === 0

  const thisClientHeader = clientName ? `Saved for ${clientName}` : 'Saved for This Client'
  const saveForClientLabel = clientName ? `Save for ${clientName}` : 'Save for client'

  return (
    <SydModal open={open} onClose={handleClose} title='Open View' size='small'>
      {isEmpty ? (
        <div className={classes.empty}>
          No saved views yet. Use &quot;Save View As...&quot; (firm-wide) or {clientName
            ? `"Save View for ${clientName}..."`
            : '"Save View for Client..."'} to create one.
        </div>
      ) : (
        <DndProvider backend={HTML5Backend}>
          <div className={classes.scroll}>
            {clientId ? (
              <ThisClientSection
                configs={thisClient}
                clientId={clientId}
                clientName={clientName}
                activeConfigId={activeConfigId}
                pendingUnassignId={pendingUnassignId}
                onLoad={handleLoad}
                onRemove={handleRemoveFromClient}
                onReorderCommit={handleReorderCommit}
                classes={classes}
                header={thisClientHeader}
              />
            ) : null}

            <MyViewsSection
              configs={myViews}
              classes={classes}
              activeConfigId={activeConfigId}
              deletingId={deletingId}
              pendingAssignId={pendingAssignId}
              canSaveForClient={!!clientId}
              saveForClientLabel={saveForClientLabel}
              onLoad={handleLoad}
              onSaveForClient={handleSaveForClient}
              onDelete={handleDelete}
            />
          </div>
        </DndProvider>
      )}
      <SydModalActions>
        <SydButton variant='outline' size='sm' onClick={handleClose}>Close</SydButton>
      </SydModalActions>
    </SydModal>
  )
}

function ThisClientSection ({
  configs,
  clientId,
  clientName,
  activeConfigId,
  pendingUnassignId,
  onLoad,
  onRemove,
  onReorderCommit,
  classes,
  header
}) {
  // localOrder holds the in-progress drag arrangement as an array of
  // explorerConfigurationIds. While set, it overrides the prop order so
  // mid-drag refetches don't snap the rows back. Cleared once the commit
  // promise resolves (server has caught up).
  const [localOrder, setLocalOrder] = useState(null)

  const renderedConfigs = useMemo(() => {
    if (!localOrder) return configs
    const byId = new Map(configs.map(c => [c.explorerConfigurationId, c]))
    return localOrder.map(id => byId.get(id)).filter(Boolean)
  }, [configs, localOrder])

  // When the upstream list changes shape (e.g. an unassign removed a
  // row, or a new view was assigned) without us being mid-drag, sync
  // localOrder so it stays valid. Cheap; only fires when ids change.
  useEffect(() => {
    if (!localOrder) return
    const currentIds = configs.map(c => c.explorerConfigurationId).sort()
    const localIds = [...localOrder].sort()
    const sameSet = currentIds.length === localIds.length &&
      currentIds.every((id, i) => id === localIds[i])
    if (!sameSet) setLocalOrder(null)
  }, [configs, localOrder])

  const moveItem = useCallback((from, to) => {
    setLocalOrder(prev => {
      const baseline = prev || renderedConfigs.map(c => c.explorerConfigurationId)
      const copy = [...baseline]
      const [moved] = copy.splice(from, 1)
      copy.splice(to, 0, moved)
      return copy
    })
  }, [renderedConfigs])

  const handleDropEnd = useCallback(() => {
    if (!localOrder) return
    // Recompute deterministic ordinals (0, 10, 20...) and dispatch one
    // assign per row whose ordinal actually changed. Original ordinals
    // come from the prop `configs` (unaffected by localOrder reshuffling).
    const ordinalById = new Map(configs.map(c => [c.explorerConfigurationId, c.ordinal]))
    const updates = []
    localOrder.forEach((id, i) => {
      const nextOrdinal = i * ORDINAL_GAP
      if (ordinalById.get(id) !== nextOrdinal) {
        updates.push({ id, ordinal: nextOrdinal })
      }
    })
    if (updates.length === 0) {
      setLocalOrder(null)
      return
    }
    onReorderCommit(updates).finally(() => setLocalOrder(null))
  }, [localOrder, configs, onReorderCommit])

  if (configs.length === 0) {
    return (
      <div className={classes.section}>
        <div className={classes.sectionHeader}>{header}</div>
        <div className={classes.sectionEmpty}>
          No views saved for {clientName || 'this client'} yet.
        </div>
      </div>
    )
  }

  const canDrag = renderedConfigs.length > 1 && !!clientId

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>{header}</div>
      {renderedConfigs.map((config, idx) => {
        const isActive = config.explorerConfigurationId === activeConfigId
        const showStar = idx === 0
        const rowAction = (
          <SydButton
            variant='outline'
            size='xs'
            disabled={pendingUnassignId === config.explorerConfigurationId}
            processing={pendingUnassignId === config.explorerConfigurationId}
            onClick={(e) => onRemove(e, config)}
          >
            Remove
          </SydButton>
        )
        return canDrag ? (
          <DraggableConfigRow
            key={config.explorerConfigurationId}
            config={config}
            index={idx}
            classes={classes}
            isActive={isActive}
            showStar={showStar}
            onLoad={() => onLoad(config)}
            rowAction={rowAction}
            moveItem={moveItem}
            onDropEnd={handleDropEnd}
          />
        ) : (
          <ConfigRow
            key={config.explorerConfigurationId}
            config={config}
            classes={classes}
            isActive={isActive}
            showStar={showStar}
            onLoad={() => onLoad(config)}
            rowAction={rowAction}
          />
        )
      })}
    </div>
  )
}

ThisClientSection.propTypes = {
  configs: PropTypes.array.isRequired,
  clientId: PropTypes.number,
  clientName: PropTypes.string,
  activeConfigId: PropTypes.number,
  pendingUnassignId: PropTypes.number,
  onLoad: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onReorderCommit: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  header: PropTypes.string.isRequired
}

function MyViewsSection ({
  configs,
  classes,
  activeConfigId,
  deletingId,
  pendingAssignId,
  canSaveForClient,
  saveForClientLabel,
  onLoad,
  onSaveForClient,
  onDelete
}) {
  if (configs.length === 0) {
    return (
      <div className={classes.section}>
        <div className={classes.sectionHeader}>My Views</div>
        <div className={classes.sectionEmpty}>None.</div>
      </div>
    )
  }

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>My Views</div>
      {configs.map((config) => (
        <ConfigRow
          key={config.explorerConfigurationId}
          config={config}
          classes={classes}
          isActive={config.explorerConfigurationId === activeConfigId}
          isDeleting={config.explorerConfigurationId === deletingId}
          showStar={!!config.isDefault}
          onLoad={() => onLoad(config)}
          rowAction={canSaveForClient
            ? (
              <SydButton
                variant='outline'
                size='xs'
                disabled={pendingAssignId === config.explorerConfigurationId}
                processing={pendingAssignId === config.explorerConfigurationId}
                onClick={(e) => onSaveForClient(e, config)}
              >
                {saveForClientLabel}
              </SydButton>
            )
            : null}
          trailingDeleteSlot={(
            <IconButton
              className={classes.deleteBtn}
              size='small'
              onClick={(e) => onDelete(e, config)}
              title='Delete view'
            >
              <Icon name='close' customSize={14} />
            </IconButton>
          )}
        />
      ))}
    </div>
  )
}

MyViewsSection.propTypes = {
  configs: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  activeConfigId: PropTypes.number,
  deletingId: PropTypes.number,
  pendingAssignId: PropTypes.number,
  canSaveForClient: PropTypes.bool,
  saveForClientLabel: PropTypes.string.isRequired,
  onLoad: PropTypes.func.isRequired,
  onSaveForClient: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}

OpenViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientId: PropTypes.number,
  clientName: PropTypes.string,
  activeConfigId: PropTypes.number,
  onLoadConfig: PropTypes.func.isRequired,
  onDeleteConfig: PropTypes.func.isRequired
}
